import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { BaseView } from '@/components/base'
import { CurrencyItem } from '../CurrencyItem'
import { SelectBlockchainModal } from '@/features/currency-deposit/components/SelectBlockchainModal'
import {
  BlockchainCatalogsDTO,
  useGetBlockchainsCatalogs,
  usePrefetchBlockchainsCatalogs,
} from '@/domain/blockchains'
import { WalletCurrency } from '@/domain/wallet'
import { useUserStore } from '@/store/useUserStore'

export const ChooseCryptoCurrencyList = () => {
  const [selectedWallet, setSelectedWallet] = useState<WalletCurrency>()
  const [walletClickCount, setWalletClickCount] = useState(0)
  const [isBlockchainModalOpen, setIsBlockchainModalOpen] = useState(false)

  const navigate = useNavigate()
  const currencyList = useUserStore((state) => state.wallet?.completeCurrencies)

  // Prefetch blockchains for all currencies to avoid delays
  const currencyCodes = useMemo(
    () => currencyList?.map((currency) => currency.code) || [],
    [currencyList],
  )
  usePrefetchBlockchainsCatalogs(currencyCodes)

  const { data: blockchainsData, isSuccess: hasSuccessOnLoadBlockchains } =
    useGetBlockchainsCatalogs(
      {
        currency_isin: selectedWallet?.code ?? '',
      },
      { enabled: !!selectedWallet },
    )

  const blockchains = (blockchainsData as any)?.blockchains || []

  // Function to navigate to the deposit page
  // Equivalent of redirectToCurrencyDepositDetailsPage from RN (lines 33-57)
  const redirectToCurrencyDepositDetailsPage = (
    blockchain: BlockchainCatalogsDTO,
  ) => {
    if (!selectedWallet) {
      return
    }

    // Build data object exactly as in RN (lines 41-49)
    const depositDetailsParams = {
      ...selectedWallet,
      networkImageUrl: blockchain.img_url || '',
      networkName: blockchain.name || '',
      networkArrivalTime: blockchain.arrival_time || undefined,
      networkCode: blockchain.code || '',
      selectedCurrencyImageUrl: selectedWallet.icon,
      selectedCurrencyReferenceColor: selectedWallet.avatarBGRefColor,
    }

    // Navigate to deposit page with data
    // In web we pass via state (in RN via URL params)
    navigate('/currency-deposit', {
      state: {
        currency: depositDetailsParams,
        blockchains: blockchains,
        selectedBlockchain: blockchain,
      },
    })
  }

  // Handler for blockchain selection from modal
  const handleBlockchainSelect = (blockchain: BlockchainCatalogsDTO) => {
    setIsBlockchainModalOpen(false)
    redirectToCurrencyDepositDetailsPage(blockchain)
  }

  // Effect for automatic navigation or showing modal
  // Equivalent of useEffect from RN (lines 82-91)
  // This effect is responsible for opening the bottom sheet or redirecting
  // automatically after the blockchains for the selected currency are successfully loaded.
  //
  // The `walletClickCount` is used as a dependency to ensure this effect
  // runs even when the user clicks on the same currency multiple times.
  // Without it, `useEffect` would not re-trigger because `blockchains` and `selectedWallet` would remain unchanged.
  useEffect(() => {
    if (hasSuccessOnLoadBlockchains && blockchains) {
      if (blockchains?.length === 1) {
        return redirectToCurrencyDepositDetailsPage(blockchains[0])
      }

      setIsBlockchainModalOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSuccessOnLoadBlockchains, blockchains, walletClickCount])

  return (
    <>
      <BaseView gap={8} style={{ paddingBottom: 70 }}>
        {currencyList?.map?.((item, index) => (
          <CurrencyItem
            key={`${item.id}-${index}`}
            {...item}
            onPress={() => {
              setSelectedWallet(item)
              setWalletClickCount((prev) => prev + 1)
            }}
          />
        ))}
      </BaseView>

      {/* Modal for blockchain selection */}
      <SelectBlockchainModal
        isOpen={isBlockchainModalOpen}
        onClose={() => setIsBlockchainModalOpen(false)}
        blockchains={blockchains}
        currencyCode={selectedWallet?.code || ''}
        onSelect={handleBlockchainSelect}
        selectedBlockchain={undefined}
      />
    </>
  )
}
